'use client';

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { getApiBaseUrl } from '@/utils/apiBaseUrl';

/**
 * Single entry-point for logout.
 *  1. POST /api/auth/logout — backend clears the httpOnly cookie and
 *     destroys the Redis session. Awaited so the browser commits the
 *     cleared cookie before we navigate; otherwise the next request
 *     (RSC fetch for `/`) carries the still-valid cookie and the
 *     middleware bounces us back to /home.
 *  2. authStore.markAnon() — flips client state to anonymous.
 *  3. queryClient.clear() — drops the entire React Query cache so the
 *     next paint cannot read authed data.
 *  4. window.location.replace('/') — full reload onto the marketing
 *     home, guaranteeing every client-side cache and provider tree is
 *     re-bootstrapped without the previous user's data.
 */
export function useLogout(): () => Promise<void> {
  const queryClient = useQueryClient();
  const markAnon = useAuthStore((s) => s.markAnon);

  return useCallback(async () => {
    try {
      await fetch(`${getApiBaseUrl()}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Requested-With': 'shot' },
      });
    } catch {
      // Even if the network request fails, fall through to the
      // client-side teardown; users should be able to log out offline.
    }
    markAnon();
    queryClient.clear();
    if (typeof window !== 'undefined') {
      // Drop @telegram-apps/sdk's cached launch params. After a TMA→web
      // handoff the SDK persists tgWebAppData under
      // sessionStorage['tapps/launchParams']. Without clearing it,
      // AuthBootstrap re-reads the cached initData on the next mount
      // and POSTs /api/auth/tma — silently re-logging the user back in.
      try { sessionStorage.removeItem('tapps/launchParams'); } catch { /* private mode */ }
      window.location.replace('/');
    }
  }, [markAnon, queryClient]);
}
